import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { resolveApiErrorMessage } from '@/api/resolveApiErrorMessage';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAddCustomDomainMutation } from '@/api/customDomains/useWorkspaceCustomDomains';

export function CustomDomainAddForm() {
  const { t } = useTranslation('customDomains');
  const addMutation = useAddCustomDomainMutation();
  const [hostname, setHostname] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  function submit(formEvent: FormEvent<HTMLFormElement>) {
    formEvent.preventDefault();
    const trimmedHostname = hostname.trim();
    if (trimmedHostname.length === 0) {
      return;
    }
    setErrorMessage('');
    addMutation.mutate(trimmedHostname, {
      onSuccess: function clearForm() {
        setHostname('');
      },
      onError: function showInlineError(addError) {
        const backendMessage = resolveApiErrorMessage(addError);
        setErrorMessage(backendMessage.length > 0 ? backendMessage : t('add.error'));
      },
    });
  }

  return (
    <form onSubmit={submit} noValidate>
      <div className="space-y-1.5">
        <Label htmlFor="custom-domain-hostname">{t('add.label')}</Label>
        <div className="flex flex-col sm:flex-row gap-3">
          <Input
            id="custom-domain-hostname"
            value={hostname}
            hasError={errorMessage.length > 0}
            placeholder={t('add.placeholder')}
            autoComplete="off"
            autoCapitalize="none"
            spellCheck={false}
            aria-describedby={errorMessage.length > 0 ? 'custom-domain-hostname-error' : undefined}
            onChange={function updateHostname(changeEvent) {
              setHostname(changeEvent.target.value);
            }}
          />
          <Button type="submit" isLoading={addMutation.isPending} className="shrink-0">
            {t('add.submit')}
          </Button>
        </div>
        {errorMessage.length > 0 ? (
          <p
            id="custom-domain-hostname-error"
            role="alert"
            className="text-xs font-medium text-zooerrortext"
          >
            {errorMessage}
          </p>
        ) : (
          <p className="text-xs text-zoomuted">{t('add.hint')}</p>
        )}
      </div>
    </form>
  );
}
